import { action, computed, makeObservable, observable } from 'mobx';
import { IResponseTakerGoSettings, ITakerGoSettingsErrors, TTakerGoSettings, takerGoSettingsCast } from '../types/takerGo';
import { defaultTakerGoSetting } from '../constants/takerGo';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';

class TakerGoStore {
  constructor() {
    makeObservable(this);
  }

  @observable public settings: TTakerGoSettings = defaultTakerGoSetting;
  @observable public validationErrors: ITakerGoSettingsErrors | null = null;
  @observable public loadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public isDataEdited: boolean = false;

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loadingStatus);
  }

  @action public fetchTakerGoSettings = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const settings: IResponseTakerGoSettings = await client.get('v3/integrations/takergo');

      this.settings = takerGoSettingsCast(settings);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'takerGoStore fetchTakerGoSettings' });
    }
  };

  @action public changeSettings = (data: Partial<TTakerGoSettings>) => {
    this.settings = { ...this.settings, ...data };
    this.isDataEdited = true;
  };

  @action public updateTakerGoSettings = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const params = {
        takergo_order_status: this.settings.orderStatus,
        dook_api_key: this.settings.takerFleetApiKey,
        yallow_api_key: this.settings.yallowApiKey,
      };

      await client.post('v3/integrations/takergo', params);

      this.isDataEdited = false;
      this.loadingStatus = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      const { dook_api_key, yallow_api_key } = error.response.data.errors;

      this.loadingStatus = LoadingStatus.Error;
      this.validationErrors = { takerFleetApi: dook_api_key, yallowApiKey: yallow_api_key };

      if (dook_api_key || yallow_api_key) return;
      logger.error({ error, place: 'takerGoStore updateTakerGoSettings' });
    }
  };

  @action public changeValidationErrors = (validationErrors: Partial<ITakerGoSettingsErrors>) => {
    this.validationErrors = { ...this.validationErrors, ...validationErrors };
  };

  @action public reset = () => {
    this.settings = defaultTakerGoSetting;
    this.loadingStatus = LoadingStatus.Init;
    this.isDataEdited = false;
  };
}

const takerGoStore = new TakerGoStore();

export default takerGoStore;
